import React from "react";
import { ArrowRight, Building, Compass, Globe, Layers, ShieldCheck, SlidersHorizontal as Sliders, Sparkles, TrendingUp } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const PROPERTY_CATEGORIES = ["Boutique Hotels", "5-Star Resorts", "Business Hotels", "Heritage Havelis", "Eco-Lodges", "Island Resorts", "Luxury Villas", "Urban Hotels", "Budget Chains"];
const METHODOLOGY_MODULES = [{
  icon: TrendingUp,
  title: "OTA channel optimization",
  description: "Algorithmic ranking, listing quality, and commission compression across top booking engines"
}, {
  icon: Sliders,
  title: "Dynamic pricing discipline",
  description: "Demand-indexed rate structures responsive to lead times, local events, and pace trends"
}, {
  icon: Layers,
  title: "Distribution tech integration",
  description: "Channel manager audits, 2-way PMS synchronization, and direct rate parity protection"
}];
export default function AuditedPerformanceHero() {
  return <section id="auditedperformancehero" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 border-b border-border overflow-hidden">
      {/* Subtle architectural background texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(hsl(222_47%_7%)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Main Hero Column (7 cols) */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div>
              {/* Badge cluster */}
              <div className="flex flex-wrap items-center gap-2.5 mb-6">
                <Badge variant="outline" className="bg-accent/40 border-secondary/40 text-foreground font-medium py-1 px-3 text-xs tracking-wider uppercase inline-flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-secondary" />
                  Audited Performance Library
                </Badge>
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-secondary inline-block" />
                  HotelRevBaBa Field Data
                </span>
              </div>

              {/* H1 Title */}
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-6">
                Real property turnarounds across every hotel category.
              </h1>

              {/* Subtext */}
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
                Explore how hands-on OTA distribution, structured channel calibration, and rate discipline translate into sustained RevPAR growth for independent properties and multi-key portfolios.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 mb-12">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold group">
                  <Link to="#" className="inline-flex items-center gap-2">
                    Book a consultation
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium">
                  <Link to="#">
                    Explore service pillars
                  </Link>
                </Button>
              </div>
            </div>

            {/* Property Category Tag Strip */}
            <div className="pt-6 border-t border-border/80">
              <div className="flex items-center justify-between mb-3.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Property Categories Documented
                </span>
                <span className="text-xs font-medium text-secondary">
                  10+ Verticals
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PROPERTY_CATEGORIES.map((category, i) => <span key={category} data-index={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted/70 hover:bg-muted text-xs font-medium text-foreground transition-colors border border-border/50">
                    <Building className="size-3 text-muted-foreground/70" />
                    {category}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Sidebar Architecture Card & Stats (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border border-border bg-card shadow-sm rounded-xl overflow-hidden">
              <CardHeader className="border-b border-border/80 bg-muted/20 pb-5 pt-6 px-6 sm:px-7">
                <div className="flex items-center gap-2.5 mb-1">
                  <span className="h-4 w-1 bg-secondary rounded-full inline-block" />
                  <span className="text-xs uppercase font-bold tracking-widest text-muted-foreground">
                    Methodology Framework
                  </span>
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                  Revenue Architecture Methodology
                </CardTitle>
              </CardHeader>

              <CardContent className="p-6 sm:p-7 space-y-5">
                {METHODOLOGY_MODULES.map((module, i) => {
                const Icon = module.icon;
                return <div key={module.title} data-index={i} className="group flex items-start gap-4 p-3.5 rounded-lg border border-transparent hover:border-border hover:bg-muted/30 transition-all">
                      <div className="size-10 rounded-md bg-accent flex items-center justify-center shrink-0 border border-secondary/20 group-hover:border-secondary/40 transition-colors">
                        <Icon className="size-5 text-foreground" />
                      </div>
                      <div className="space-y-1">
                        <h2 className="text-sm font-semibold text-foreground tracking-tight">
                          {module.title}
                        </h2>
                        <p className="text-xs text-muted-foreground leading-normal">
                          {module.description}
                        </p>
                      </div>
                    </div>;
              })}
              </CardContent>

              <Separator />

              <CardFooter className="bg-muted/15 px-6 sm:px-7 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ShieldCheck className="size-4 text-secondary shrink-0" />
                  <span>Advisory led by Sunil Singh</span>
                </div>
                <Button asChild variant="link" className="p-0 h-auto text-xs font-semibold text-foreground hover:text-secondary inline-flex items-center gap-1 group justify-start sm:justify-end">
                  <Link to="#">
                    Request a property audit
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Stat and Scope Indicators */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-border bg-card shadow-2xs">
                <div className="flex items-center gap-2 mb-1.5">
                  <Globe className="size-4 text-secondary" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Multi-Market Reach
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Leisure, corporate &amp; heritage destinations
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-card shadow-2xs">
                <div className="flex items-center gap-2 mb-1.5">
                  <Compass className="size-4 text-secondary" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Direct Alignment
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Customized per hotel asset &amp; inventory scale
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}